import { useEffect, useState } from "react";
import { useMutation, useQuery } from "@tanstack/react-query";
import { getJson } from "@/lib/http";
import { apiRequest, queryClient } from "@/lib/queryClient";
import { useToast } from "@/hooks/use-toast";
import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from "@/components/ui/table";
import { APP_PLATFORMS, APP_VERSION_MESSAGE_MAX_LENGTH, type AppPlatform } from "@shared/app-version";

interface Recommendation {
  platform: AppPlatform;
  recommendedVersion: string;
  storeUrl: string | null;
  message: string | null;
  updatedAt: string;
}

interface OperatorAppVersion {
  operatorId: number;
  operatorName: string;
  username: string;
  appVersion: string | null;
  osName: string | null;
  deviceId: string | null;
  lastSeenAt: string;
  platform: AppPlatform | null;
  recommendedVersion: string | null;
  behind: boolean;
}

interface AppVersionsResponse {
  recommendations: Recommendation[];
  operators: OperatorAppVersion[];
}

const QUERY_KEY = ["/api/admin/operators/app-versions"];

function platformLabel(platform: AppPlatform): string {
  switch (platform) {
    case "ios":
      return "iPhone";
    case "android":
      return "Android";
    default: {
      const unhandled: never = platform;
      return String(unhandled);
    }
  }
}

function RecommendationForm({ platform, current }: { platform: AppPlatform; current: Recommendation | undefined }) {
  const { toast } = useToast();
  const [version, setVersion] = useState(current?.recommendedVersion ?? "");
  const [storeUrl, setStoreUrl] = useState(current?.storeUrl ?? "");
  const [message, setMessage] = useState(current?.message ?? "");

  useEffect(() => {
    setVersion(current?.recommendedVersion ?? "");
    setStoreUrl(current?.storeUrl ?? "");
    setMessage(current?.message ?? "");
  }, [current?.recommendedVersion, current?.storeUrl, current?.message]);

  const save = useMutation({
    mutationFn: () => apiRequest(`/api/admin/operators/app-versions/${platform}`, "PUT", {
      recommendedVersion: version,
      storeUrl: storeUrl || null,
      message: message || null,
    }),
    onSuccess: () => {
      void queryClient.invalidateQueries({ queryKey: QUERY_KEY });
      toast({ title: `${platformLabel(platform)} recommendation saved` });
    },
    onError: (error: Error) => toast({ title: "Could not save", description: error.message, variant: "destructive" }),
  });

  const clear = useMutation({
    mutationFn: () => apiRequest(`/api/admin/operators/app-versions/${platform}`, "DELETE"),
    onSuccess: () => {
      void queryClient.invalidateQueries({ queryKey: QUERY_KEY });
      toast({ title: `${platformLabel(platform)} banner turned off` });
    },
    onError: (error: Error) => toast({ title: "Could not clear", description: error.message, variant: "destructive" }),
  });

  const idPrefix = `app-version-${platform}`;

  return (
    <div className="space-y-3 rounded-md border p-4">
      <div className="flex items-center justify-between">
        <h3 className="font-semibold">{platformLabel(platform)}</h3>
        {current ? <Badge variant="secondary">Banner on</Badge> : <Badge variant="outline">No banner</Badge>}
      </div>
      <div className="space-y-1">
        <Label htmlFor={`${idPrefix}-version`}>Recommended version</Label>
        <Input id={`${idPrefix}-version`} placeholder="1.4.0" value={version} onChange={(e) => setVersion(e.target.value)} />
      </div>
      <div className="space-y-1">
        <Label htmlFor={`${idPrefix}-url`}>Store link (optional)</Label>
        <Input id={`${idPrefix}-url`} placeholder="https://…" value={storeUrl} onChange={(e) => setStoreUrl(e.target.value)} />
      </div>
      <div className="space-y-1">
        <Label htmlFor={`${idPrefix}-message`}>Banner message (optional)</Label>
        <Textarea
          id={`${idPrefix}-message`}
          maxLength={APP_VERSION_MESSAGE_MAX_LENGTH}
          placeholder="Shown instead of the default text"
          value={message}
          onChange={(e) => setMessage(e.target.value)}
        />
      </div>
      <div className="flex gap-2">
        <Button onClick={() => save.mutate()} disabled={!version.trim() || save.isPending}>
          Save
        </Button>
        {current && (
          <Button variant="outline" onClick={() => clear.mutate()} disabled={clear.isPending}>
            Turn off banner
          </Button>
        )}
      </div>
    </div>
  );
}

export default function AppVersionsPanel() {
  const { data, isLoading, error } = useQuery<AppVersionsResponse>({
    queryKey: QUERY_KEY,
    queryFn: () => getJson<AppVersionsResponse>("/api/admin/operators/app-versions"),
  });

  if (isLoading) return <p className="text-sm text-muted-foreground">Loading app versions…</p>;
  if (error || !data) return <p className="text-sm text-destructive">Could not load app versions.</p>;

  const behindCount = data.operators.filter((row) => row.behind).length;

  return (
    <div className="space-y-6">
      <p className="text-sm text-muted-foreground">
        Operators on an older app see a dismissible "Update available" banner once a day. Nobody is blocked.
      </p>
      <div className="grid gap-4 md:grid-cols-2">
        {APP_PLATFORMS.map((platform) => (
          <RecommendationForm
            key={platform}
            platform={platform}
            current={data.recommendations.find((row) => row.platform === platform)}
          />
        ))}
      </div>

      <div className="space-y-2">
        <h3 className="font-semibold">
          Operator phones {behindCount > 0 && <span className="text-amber-700">· {behindCount} behind</span>}
        </h3>
        <Table>
          <TableHeader>
            <TableRow>
              <TableHead>Operator</TableHead>
              <TableHead>Phone</TableHead>
              <TableHead>App version</TableHead>
              <TableHead>Status</TableHead>
              <TableHead>Last seen</TableHead>
            </TableRow>
          </TableHeader>
          <TableBody>
            {data.operators.length === 0 && (
              <TableRow>
                <TableCell colSpan={5} className="text-muted-foreground">No phones have reported in the last 90 days.</TableCell>
              </TableRow>
            )}
            {data.operators.map((row) => (
              <TableRow key={row.operatorId}>
                <TableCell>
                  <div className="font-medium">{row.operatorName}</div>
                  <div className="text-xs text-muted-foreground">{row.username}</div>
                </TableCell>
                <TableCell>{row.platform ? platformLabel(row.platform) : row.osName ?? "Unknown"}</TableCell>
                <TableCell>{row.appVersion ?? "Unknown"}</TableCell>
                <TableCell>
                  {row.behind ? (
                    <Badge className="bg-amber-100 text-amber-900 hover:bg-amber-100">Behind {row.recommendedVersion}</Badge>
                  ) : row.recommendedVersion ? (
                    <Badge variant="secondary">Up to date</Badge>
                  ) : (
                    <span className="text-sm text-muted-foreground">No recommendation</span>
                  )}
                </TableCell>
                <TableCell>{new Date(row.lastSeenAt).toLocaleString()}</TableCell>
              </TableRow>
            ))}
          </TableBody>
        </Table>
      </div>
    </div>
  );
}
